import { useState, useEffect, useRef } from 'react';

/**
 * Options for the usePolling hook
 */
export interface PollingOptions<T> {
  /**
   * Function that fetches the data
   */
  fetchFn: () => Promise<T>;
  
  /**
   * Initial data value
   */
  initialData?: T;
  
  /**
   * Polling interval in milliseconds
   */
  interval: number;
  
  /**
   * Dependencies that will trigger a refetch when changed
   */
  dependencies?: any[];
  
  /**
   * Whether to enable polling
   * @default true
   */
  enabled?: boolean;
  
  /**
   * Whether to only update state if data has changed
   * @default true
   */
  onlyUpdateIfChanged?: boolean;
  
  /**
   * Function to compare old and new data to determine if state should update
   * By default uses JSON.stringify for comparison
   */
  compareDataFn?: (oldData: T | undefined, newData: T) => boolean;
  
  /**
   * Whether to perform silent updates without changing loading/refreshing states
   * Useful to prevent UI flickering during background polls
   * @default false
   */
  silentUpdates?: boolean;
}

/**
 * Hook that implements polling with configurable interval and error handling
 */
export function usePolling<T>(options: PollingOptions<T>) {
  const {
    fetchFn,
    initialData,
    interval,
    dependencies = [],
    enabled = true,
    onlyUpdateIfChanged = true,
    compareDataFn,
    silentUpdates = false
  } = options;

  const [data, setData] = useState<T | undefined>(initialData);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  const dataRef = useRef<T | undefined>(initialData);
  
  const shouldUpdateData = (oldData: T | undefined, newData: T): boolean => {
    if (!onlyUpdateIfChanged) return true;
    
    if (compareDataFn) {
      return compareDataFn(oldData, newData);
    }
    
    return JSON.stringify(oldData) !== JSON.stringify(newData);
  };

  const fetchData = async (isBackgroundRefresh = false, silent = false) => {
    if (!enabled) return;
    
    // For completely silent updates, don't set any loading states
    const shouldBeSilent = silent || (isBackgroundRefresh && silentUpdates);
    
    try {
      // Only set loading/refreshing states if not silent
      if (!shouldBeSilent) {
        if (!isBackgroundRefresh) {
          setLoading(true);
        } else {
          // Only show refreshing state for background polling when we have actual data
          if (dataRef.current && (!Array.isArray(dataRef.current) || Array.isArray(dataRef.current) && dataRef.current.length > 0)) {
            setRefreshing(true);
          }
        }
      }
      
      const newData = await fetchFn();
      
      if (shouldUpdateData(dataRef.current, newData)) {
        setData(newData);
        dataRef.current = newData;
        setLastUpdate(new Date());
      }
      
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      // Only set error state if not silent
      if (!shouldBeSilent) {
        setError(errorMessage);
      }
      console.error('Error in usePolling:', err);
    } finally {
      // Only update loading/refreshing states if not silent
      if (!shouldBeSilent) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };
  
  useEffect(() => {
    // Fetch data initially (never silent for first fetch)
    fetchData(false, false);
    
    // Set up polling only if enabled and interval is positive
    if (enabled && interval > 0) {
      const pollInterval = setInterval(() => {
        // Background refreshes can be silent based on the option
        fetchData(true, silentUpdates);
      }, interval);
      
      // Clean up on unmount or dependencies change
      return () => clearInterval(pollInterval);
    }
    
    return undefined;
  }, [interval, enabled, silentUpdates, ...dependencies]);
  
  return {
    data,
    loading,
    refreshing,
    error,
    lastUpdate,
    refetch: (silent = false) => fetchData(false, silent),
    silentRefetch: () => fetchData(false, true),
    /** 
     * Allows manual update of data without fetching
     * Useful when you've updated data locally and want to reflect that
     */
    updateData: (newData: T) => {
      setData(newData);
      dataRef.current = newData;
      setLastUpdate(new Date());
    }
  };
} 